import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay, Button, Dialog, type FocusZoneHookSettings} from '@primer/react'
import React, {useId, useRef, useState} from 'react'

import {SelectBody} from './components/SimpleSelectBody'
import {SelectFooter} from './components/SimpleSelectFooter'
import {SelectHeader} from './components/SimpleSelectHeader'
import styles from './SimpleSelect.module.css'
import type {FooterButtons, Items, SelectProps, SimpleSelectDialogProps} from './types'

export function SimpleSelect({
  variant = 'overlay',
  items,
  label,
  title = 'Select an item',
  selectionVariant,
  onSelect,
  renderText,
  onSave,
  onCancel,
  selectable,
  buttonProps,
  outsideClick = 'save',
  onEscape = 'save',
  focusTarget = 'first-target',
  dialogProps,
  footerButtons,
}: SelectProps) {
  const [open, setOpen] = useState(false)
  const [staticItems, setStaticItems] = useState<Items[]>()
  const [selectItems, setSelectItems] = useState<Items[]>([])

  const firstItem = useRef<HTMLLIElement>(null)
  const headingId = useId()
  const selectedItems = selectable ? selectItems : items.filter(item => item.selected)

  const btn = useRef<HTMLButtonElement>(null)

  if (!staticItems) {
    setStaticItems(items)
    setSelectItems(items.filter(item => item.selected))
  }

  const handleAction = (action: 'save' | 'cancel', received?: Items[]) => {
    if (action === 'save' && (onSave || selectable)) {
      const currentItems = selectable ? received || selectItems : items.filter(item => item.selected)
      // TODO: Improve, we shouldn't need to iterate through all items here
      const updatedItems = items.map(item => {
        const isSelected = currentItems.find(selectedItem => selectedItem.id === item.id)
        return isSelected ? {...item, selected: true} : {...item, selected: false}
      })

      setStaticItems(updatedItems)
      if (onSave) onSave(updatedItems)
    } else if (action === 'cancel' && onCancel) {
      onCancel(staticItems || items)
    } else if (action === 'cancel' && selectable) {
      setSelectItems(staticItems?.filter(item => item.selected) || [])
    }
    setOpen(false)
  }

  const selectSave = () => handleAction('save')
  const selectCancel = () => handleAction('cancel')

  const handleControlledItems = (selection: Items[]) => {
    setSelectItems(selection)

    if (selectItems) selectable?.(selection)
    if (selectionVariant === 'single') {
      handleAction('save', selection)
    }
  }

  const onClose = (gesture: string) => {
    if (gesture === 'click-outside' || gesture === 'anchor-click' || gesture === 'close-button') {
      handleAction(outsideClick)
    } else if (gesture === 'escape') {
      handleAction(onEscape, selectItems)
    }
  }

  return (
    <>
      <Button
        trailingAction={variant === 'overlay' ? TriangleDownIcon : null}
        ref={btn}
        variant="default"
        onClick={() => setOpen(!open)}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...buttonProps}
      >
        {renderText
          ? renderText()
          : `${label}${!selectedItems.length ? '' : `: ${selectedItems.map(item => item.label).join(', ')}`}`}
      </Button>
      <SelectContainer
        headingId={headingId}
        open={open}
        anchorRef={btn}
        onClose={onClose}
        variant={variant}
        dialogProps={dialogProps}
        title={title}
        selectSave={selectSave}
        selectCancel={selectCancel}
        footerButtons={footerButtons}
        focusTarget={focusTarget}
        firstItem={firstItem}
      >
        <div data-container={variant} className={styles.Wrapper}>
          {variant === 'overlay' ? <SelectHeader id={headingId} title={title} onClose={selectCancel} /> : null}
          <div className={styles.SelectBodyWrapper}>
            <ActionList role="listbox" selectionVariant={selectionVariant} aria-label="Selection">
              <SelectBody
                selectionVariant={selectionVariant}
                items={selectable && staticItems ? staticItems : items}
                onSelect={
                  selectionVariant === 'single' && !onSave && !selectable
                    ? selection => {
                        onSelect?.(selection)
                        setOpen(false)
                      }
                    : onSelect
                }
                controlled={selectable ? handleControlledItems : undefined}
                ref={focusTarget === 'first-item' ? firstItem : null}
              />
            </ActionList>
          </div>
          {onSave && variant !== 'modal' ? (
            <SelectFooter onSave={selectSave} onCancel={selectCancel} footerButtons={footerButtons} />
          ) : null}
        </div>
      </SelectContainer>
    </>
  )
}

export function SelectContainer({
  open,
  anchorRef: btn,
  headingId,
  focusTarget,
  firstItem,
  onClose,
  children,
  variant,
  dialogProps,
  title,
  selectSave,
  selectCancel,
  footerButtons,
  focusZoneSettings,
}: {
  open: boolean
  anchorRef: React.RefObject<HTMLButtonElement | null>
  headingId: string
  focusTarget?: 'first-item' | 'first-target'
  firstItem?: React.RefObject<HTMLLIElement | null>
  onClose: (gesture: string) => void
  children: React.ReactNode
  variant?: 'overlay' | 'modal'
  dialogProps?: SimpleSelectDialogProps
  title?: string
  selectSave?: () => void
  selectCancel?: () => void
  footerButtons?: FooterButtons[]
  focusZoneSettings?: FocusZoneHookSettings
}) {
  return (
    <>
      {variant === 'overlay' ? (
        <AnchoredOverlay
          open={open}
          anchorRef={btn}
          renderAnchor={null}
          overlayProps={{
            role: 'dialog',
            'aria-labelledby': headingId,
            maxHeight: 'large',
            maxWidth: 'medium',
          }}
          focusZoneSettings={focusZoneSettings || {disabled: true}}
          focusTrapSettings={{initialFocusRef: focusTarget === 'first-item' ? firstItem : undefined}}
          onClose={onClose}
        >
          {children}
        </AnchoredOverlay>
      ) : (
        <>
          {open ? (
            <Dialog
              renderBody={() => <Dialog.Body className={styles.DialogBody}>{children}</Dialog.Body>}
              width="large"
              onClose={onClose}
              title={title}
              renderFooter={() => (
                <SelectFooter onSave={selectSave} onCancel={selectCancel} footerButtons={footerButtons} />
              )}
              position={{
                narrow: 'fullscreen',
                regular: 'center',
              }}
              {...dialogProps}
            />
          ) : null}
        </>
      )}
    </>
  )
}
